import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FieldArrayPath, useFieldArray, useFormState, useWatch } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { EmailVerificationStatus } from 'components/EmailVerificationStatus';
import FormattedInput from 'components/FormInputs/FormattedInput.tsx';
import SelectInput from 'components/FormInputs/SelectInput';
import TextInput from 'components/FormInputs/TextInput.tsx';

import { Relations } from 'pages/Main/PackageListing/components/RegistrationForm/utils/formConstants';
import { RegistrationFormOutputValues } from 'pages/Main/PackageListing/components/RegistrationForm/utils/validationSchema';

interface ParentFieldArrayProps {
  name: FieldArrayPath<RegistrationFormOutputValues>;
  numRequired?: number;
}

function ParentFieldArray({ name, numRequired = 0 }: ParentFieldArrayProps) {
  const fieldArray = useFieldArray({ name: name });
  const parentValues = useWatch({ name: name });
  const { errors } = useFormState<RegistrationFormOutputValues>({
    name: name,
  });

  return (
    <Stack gap={4}>
      {fieldArray.fields.map((_field, index) => (
        <Grid key={index} container spacing={2}>
          <Grid size={12}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                textDecoration: 'underline',
              }}
            >
              Parent {index + 1}
            </Typography>
            <Typography color="error" fontWeight="bold">
              {errors[name]?.[index]?.root?.message}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <SelectInput
              name={`${name}.${index}.relation`}
              label="Relation"
              options={Relations}
              sx={{
                minWidth: '100px',
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput name={`${name}.${index}.first_name`} label="First Name" />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput name={`${name}.${index}.last_name`} label="Last Name" />
          </Grid>

          <Grid size={12}>
            <Stack>
              <TextInput name={`${name}.${index}.email`} label="Email" />
              <EmailVerificationStatus verificationRequest={parentValues?.[index]?.email_verification_request} />
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormattedInput name={`${name}.${index}.cell_phone`} label="Cell Phone" format="(###) ###-####" mask="_" />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormattedInput name={`${name}.${index}.home_phone`} label="Home Phone" format="(###) ###-####" mask="_" />
          </Grid>

          <Box width="100%" />

          <Grid size={{ xs: 12, md: 4 }}>
            <FormattedInput name={`${name}.${index}.work_phone`} label="Work Phone" format="(###) ###-####" mask="_" />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput
              name={`${name}.${index}.work_extension`}
              label="Work Extension"
              disabled={!parentValues?.[index]?.work_phone}
              InputProps={{ startAdornment: <InputAdornment position="start">Ext.</InputAdornment> }}
            />
          </Grid>

          {fieldArray.fields.length > numRequired && (
            <Grid size={12}>
              <Button
                variant="contained"
                size="small"
                color="error"
                fullWidth
                onClick={() => {
                  console.log(`Deleting index: ${index}`);
                  fieldArray.remove(index);
                }}
              >
                Remove Parent {index + 1}
              </Button>
            </Grid>
          )}
        </Grid>
      ))}

      <Button
        variant="contained"
        size="small"
        fullWidth
        onClick={() => {
          fieldArray.append({
            id: uuidv4(),
            first_name: '',
            last_name: '',
            relation: '',
            email: '',
            cell_phone: '',
            home_phone: '',
            work_phone: '',
            work_extension: '',
          });
        }}
      >
        {fieldArray.fields.length > 0 ? 'Add Another Parent' : 'Add Parent'}
      </Button>
    </Stack>
  );
}

export default ParentFieldArray;
